import React from "react";
import { Link } from "react-router-dom";
import { User, Mail, Phone, Edit2, Check, X, Gift, MessageCircle } from "lucide-react";

/**
 * Dashboard hero for the logged-in customer portal — welcome card, loyalty
 * balloons strip, and the primary "Book a Party / Chat with Us" CTAs.
 */
export const CustomerDashboardHero = ({
  customer, displayName, allBookings,
  editingPhone, setEditingPhone, editPhoneInput, setEditPhoneInput, handleUpdatePhone,
  balloonProgress, freePartyEarned, BalloonIcon,
}) => {
  return (
    <>
      {/* Welcome Banner */}
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-[#f0e6d9] p-5" data-testid="customer-profile">
        <div className="flex items-center gap-4">
          {customer.picture ? (
            <img src={customer.picture} alt="" className="w-14 h-14 rounded-full border-2 border-[#E83E8C]/20" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-[#E83E8C]/10 flex items-center justify-center">
              <User size={24} className="text-[#E83E8C]" />
            </div>
          )}
          <div className="flex-1">
            <h2 className="text-xl font-bold text-[#1E1B4B]" data-testid="welcome-message">
              {allBookings.length > 0 ? `Welcome back, ${displayName}!` : `Welcome, ${displayName}!`}
            </h2>
            {customer.email && (
              <p className="text-sm text-[#475569] flex items-center gap-1">
                <Mail size={12} /> {customer.email}
              </p>
            )}
            <div className="flex items-center gap-1 mt-1">
              <Phone size={12} className="text-[#475569]" />
              {editingPhone ? (
                <div className="flex items-center gap-1">
                  <input
                    value={editPhoneInput}
                    onChange={(e) => setEditPhoneInput(e.target.value)}
                    className="text-sm border rounded px-2 py-0.5 w-32"
                    placeholder="Phone number"
                    data-testid="edit-phone-input"
                  />
                  <button onClick={handleUpdatePhone} className="text-green-500"><Check size={14} /></button>
                  <button onClick={() => setEditingPhone(false)} className="text-red-400"><X size={14} /></button>
                </div>
              ) : (
                <span className="text-sm text-[#475569]">
                  {customer.phone || "Add phone"}{" "}
                  <button onClick={() => setEditingPhone(true)} className="text-[#E83E8C]"><Edit2 size={12} /></button>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Balloon Rewards — Animated */}
      <div className="bg-gradient-to-r from-[#1E1B4B] to-[#312E81] rounded-2xl shadow-lg p-5 text-white" data-testid="loyalty-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider opacity-80">Balloon Rewards</h3>
          <Gift size={20} className="opacity-60" />
        </div>
        <div className="flex items-end justify-center gap-1 sm:gap-2 mb-3 py-2">
          {[...Array(10)].map((_, i) => (
            <BalloonIcon key={`balloon-${i}`} active={i < balloonProgress} index={i} delay={i * 0.2} />
          ))}
        </div>
        <p className="text-xs text-indigo-200 text-center">
          {freePartyEarned
            ? "You've earned a FREE Basic/Premium party! Contact us to claim."
            : `${balloonProgress}/10 balloons collected. ${10 - balloonProgress} more for a FREE party!`}
        </p>
        <p className="text-[10px] text-indigo-300/50 mt-1 text-center">
          1 balloon per booking. 10 balloons = 1 free Basic or Premium party.
        </p>
      </div>
    </>
  );
};

/**
 * Primary call-to-action row at the bottom of the customer dashboard.
 */
export const CustomerDashboardCTAs = () => (
  <div className="flex gap-3">
    <Link to="/#book-online" className="flex-1 py-3 rounded-xl bg-[#E83E8C] text-white font-semibold text-center text-sm hover:bg-[#d1356f] transition-colors" data-testid="book-party-btn">
      Book a Party
    </Link>
    <a href="https://wa.me/919930001848" target="_blank" rel="noopener noreferrer"
      className="flex-1 py-3 rounded-xl border border-[#25D366] text-[#25D366] font-medium text-center text-sm hover:bg-[#25D366]/5 transition-colors flex items-center justify-center gap-1.5">
      <MessageCircle size={14} /> Chat with Us
    </a>
  </div>
);

export default CustomerDashboardHero;
